import { Typography, Chip } from 'components/common'
import {
  Category,
  useUpdateDiscussionBoardCategoryNotificationSettingMutation
} from 'graphql/generated'
import useCategoryInputModal from './useCategoryInputModal'
import { useCollectiveContext } from 'context/CollectiveContext'
import useThreadInputModal from './useThreadInputModal'
import { useForumContext } from './ForumContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBell,
  faBellSlash,
  faBoxArchive
} from '@fortawesome/pro-solid-svg-icons'
import Spinner from 'components/Spinner'
import { useAnalyticsContext } from 'context/AnalyticsContext'

export type TCategory = Pick<
  Category,
  | 'id'
  | 'name'
  | 'description'
  | 'removable'
  | 'totalThreads'
  | 'archived'
  | 'notificationSettings'
>

const BaseCategoryHeader: React.FC = () => {
  const { collective } = useCollectiveContext()
  const { selectedCategory } = useForumContext()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const { modalTrigger: editCategoryTrigger } = useCategoryInputModal({
    category: selectedCategory
  })

  const [updateNotification, { loading: isUpdatingNotification }] =
    useUpdateDiscussionBoardCategoryNotificationSettingMutation()

  const { modalTrigger: newThreadTrigger } = useThreadInputModal({})

  return (
    <div className="space-y-2">
      {selectedCategory.archived && (
        <div className="bg-red-50 py-2 mb-4 rounded-md text-red-700 text-center">
          <FontAwesomeIcon icon={faBoxArchive} /> This category is archived.
        </div>
      )}

      <div className="w-full gap-4 flex justify-between">
        <div className="flex-1 gap-4 flex items-center">
          <Typography>{`# ${selectedCategory.name}`}</Typography>

          <Chip size="sm" className="px-4">
            {selectedCategory.totalThreads}
          </Chip>
        </div>

        <div className="flex items-center gap-4">
          <span>
            {isUpdatingNotification ? (
              <Spinner />
            ) : (
              <Typography
                color={
                  selectedCategory.notificationSettings.allowNotifications
                    ? 'warning'
                    : 'lightGray'
                }
                className="cursor-pointer"
                onClick={() => {
                  if (isUpdatingNotification) {
                    return
                  }
                  updateNotification({
                    variables: {
                      categoryId: selectedCategory.id,
                      allowNotifications:
                        !selectedCategory.notificationSettings
                          .allowNotifications
                    },
                    onCompleted: () => {
                      if (analyticsLoaded) {
                        window.analytics?.trackAll(
                          `Forum - Category Notification - Update`,
                          {
                            allowNotification:
                              !selectedCategory.notificationSettings
                                .allowNotifications,
                            categoryId: selectedCategory.id,
                            categoryName: selectedCategory.name
                          }
                        )
                      }
                    }
                  })
                }}
              >
                <FontAwesomeIcon
                  icon={
                    selectedCategory.notificationSettings.allowNotifications
                      ? faBell
                      : faBellSlash
                  }
                />
              </Typography>
            )}
          </span>

          {collective.membership?.privileged && editCategoryTrigger}

          {selectedCategory.totalThreads > 0 &&
            !selectedCategory.governance &&
            !selectedCategory.archived &&
            newThreadTrigger}
        </div>
      </div>

      <Typography color="gray" size="xs">
        {selectedCategory.description}
      </Typography>
    </div>
  )
}

export default BaseCategoryHeader
